/** Palette matching src/styles/theme.css tokens, used when the API sends color: null. */
export const CHART_COLORS = [
  '#d93188', // --primary-500
  '#1e69b3', // --secondary-600
  '#399379', // --accent-green-500
  '#d8c057', // --accent-yellow-500
  '#d87557', // --accent-orange-500
  '#3d55db', // --accent-blue-500
  '#d67852', // --alert-500
  '#6ac446', // --success-500
];

/**
 * Resolve a series/slice color, falling back to the theme palette by index.
 * @param {string | null | undefined} color - CSS color from the API
 * @param {number} index - Series or slice index
 * @returns {string} Resolved CSS color
 */
export const resolveSeriesColor = (color, index) => {
  if (color) return color;
  return CHART_COLORS[index % CHART_COLORS.length];
};

/**
 * Resolve per-category colors. Null input means "use series colors".
 * @param {string[]} categories - Category labels
 * @param {Array<string | null> | null | undefined} categoryColors - Colors aligned with categories
 * @returns {string[] | null} Resolved colors, or null when the API omitted them
 */
export const resolveCategoryColors = (categories, categoryColors) => {
  if (!categoryColors) return null;

  return (categories || []).map((_, index) => resolveSeriesColor(categoryColors[index], index));
};

/**
 * Pivot API series (values aligned on categories) into Recharts rows.
 * @param {string[]} categories - X-axis (or category-axis) labels
 * @param {ApiChartSeries[]} series - Series whose values follow categories
 * @returns {MappedChartRow[]} Recharts data rows
 */
export const buildChartRows = (categories, series) => {
  if (!Array.isArray(categories)) return [];

  const seriesList = Array.isArray(series) ? series : [];

  return categories.map((category, index) => ({
    category,
    ...Object.fromEntries(
      seriesList.map((serie) => [serie.dataKey, serie.values?.[index] ?? null]),
    ),
  }));
};

/**
 * Map API series to a stable chart-atom series interface.
 * @param {ApiChartSeries[]} series - API series
 * @returns {MappedChartSeries[]} Mapped series
 */
export const mapChartSeries = (series) => (series || []).map((serie, index) => ({
  dataKey: serie.dataKey,
  name: serie.label,
  color: resolveSeriesColor(serie.color, index),
  emphasis: Boolean(serie.emphasis),
  url: serie.url ?? null,
}));

/**
 * Map API reference lines to a stable chart-atom interface.
 * @param {ApiChartReferenceLine[]} referenceLines - API reference lines
 * @returns {MappedChartReferenceLine[]} Mapped reference lines
 */
export const mapReferenceLines = (referenceLines) => (referenceLines || []).map((line, index) => ({
  value: line.value,
  label: line.label,
  color: resolveSeriesColor(line.color, index),
}));

/**
 * Convert legendPosition to Recharts Legend props, or null when hidden.
 * @param {ApiChartLegendPosition | null | undefined} legendPosition - API legend position
 * @returns {MappedChartLegend | null} Legend config, or null for none
 */
export const getLegendConfig = (legendPosition) => {
  switch (legendPosition) {
    case 'top':
      return { layout: 'horizontal', align: 'center', verticalAlign: 'top' };
    case 'bottom':
      return { layout: 'horizontal', align: 'center', verticalAlign: 'bottom' };
    case 'left':
      return { layout: 'vertical', align: 'left', verticalAlign: 'middle' };
    case 'right':
      return { layout: 'vertical', align: 'right', verticalAlign: 'middle' };
    default:
      return null;
  }
};

/**
 * Look up a series URL by Recharts dataKey (legend click).
 * @param {MappedChartSeries[]} series - Mapped series
 * @param {string | undefined} dataKey - Clicked series dataKey
 * @returns {string | null} URL to open, or null
 */
export const getSeriesUrlByDataKey = (series, dataKey) => {
  if (!dataKey) return null;
  return series.find((serie) => serie.dataKey === dataKey)?.url ?? null;
};

/**
 * Map a chartLine API item to ChartLine props.
 * @param {ApiDashboardItemChartLine} chartLine - API line chart
 * @returns {{ rows: MappedChartRow[], series: MappedChartSeries[], referenceLines: MappedChartReferenceLine[], legend: MappedChartLegend | null }}
 */
export const mapLineChartProps = (chartLine) => ({
  rows: buildChartRows(chartLine.categories, chartLine.series),
  series: mapChartSeries(chartLine.series),
  referenceLines: mapReferenceLines(chartLine.referenceLines),
  legend: getLegendConfig(chartLine.legendPosition),
});

/**
 * Map a chartBar API item to ChartBar props.
 * @param {ApiDashboardItemChartBar} chartBar - API bar chart
 * @returns {{
 *   rows: MappedChartRow[],
 *   series: MappedChartSeries[],
 *   referenceLines: MappedChartReferenceLine[],
 *   legend: MappedChartLegend | null,
 *   layout: 'horizontal' | 'vertical',
 *   stacked: boolean,
 *   categoryColors: string[] | null,
 * }}
 */
export const mapBarChartProps = (chartBar) => ({
  rows: buildChartRows(chartBar.categories, chartBar.series),
  series: mapChartSeries(chartBar.series),
  referenceLines: mapReferenceLines(chartBar.referenceLines),
  legend: getLegendConfig(chartBar.legendPosition),
  layout: chartBar.layout,
  stacked: Boolean(chartBar.stacked),
  categoryColors: resolveCategoryColors(chartBar.categories, chartBar.categoryColors),
});

/**
 * Map a chartPie API item to ChartPie props.
 * @param {ApiDashboardItemChartPie} chartPie - API pie chart
 * @returns {{ slices: MappedPieSlice[], legend: MappedChartLegend | null, showValuesInLegend: boolean, donut: boolean }}
 */
export const mapPieChartProps = (chartPie) => ({
  slices: (chartPie.slices || []).map((slice, index) => ({
    name: slice.label,
    value: slice.value,
    color: resolveSeriesColor(slice.color, index),
  })),
  legend: getLegendConfig(chartPie.legendPosition),
  showValuesInLegend: Boolean(chartPie.showValuesInLegend),
  donut: Boolean(chartPie.donut),
});
